import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Autocomplete, Input } from '@launchpad-ui/components';

import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';
import type { FieldOptions, ParsedToken } from '../../../types/QueryBuilder';
import { AutocompletePopover } from '../AutocompletePopover';
import { useQueryBuilderContext } from '../context';
import { useFilterBarContext } from '../FilterBarContext';
import { useAutocompleteNavigation } from '../hooks/useAutocompleteNavigation';
import { useKeyboardSelectionContext } from '../hooks/useKeyboardSelection';
import {
  collectUsedExpressionTypes,
  dedupeOptionsByValue,
  expressionTypeAtOffset,
  fieldOptionsForScope,
  flattenTokens,
  focusAdjacentGap,
  focusQueryEdge,
  getFocusableCells,
  inferFieldType,
  matchScore,
  replaceToken,
  scopeForExpressionType,
} from '../utils';

import styles from './FreeTextToken.module.css';

const BASE_LOGIC_OPTIONS = [
  { value: 'AND', displayName: 'AND', category: 'Operators' as const },
  { value: 'OR', displayName: 'OR', category: 'Operators' as const },
  {
    value: 'GROUP',
    displayName: 'Logical Group',
    category: 'Operators' as const,
    description: 'Logical grouping of filter expressions to evaluate first.',
  },
];

const FREE_TEXT_SEARCH_SENTINEL = '__FREE_TEXT_SEARCH__';

/**
 * Browsers that support `field-sizing: content` size the input from CSS (see FreeTextToken.module.css),
 * so the JS fallback below would only fight the cascade — skip it when the feature is available.
 */
const SUPPORTS_FIELD_SIZING =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content');

/** Set an inline width that grows with the value. No-op when `field-sizing: content` is supported. */
function setInputWidth(el: HTMLInputElement | null, value: string): void {
  if (!el || SUPPORTS_FIELD_SIZING) {
    return;
  }
  // eslint-disable-next-line no-param-reassign -- intentional DOM mutation on the passed-in input
  el.style.width = `${Math.max(value.length, 1)}ch`;
}

interface FreeTextTokenProps {
  initialValue?: string;
  /** Field options used to populate autocomplete suggestions. */
  options?: FieldOptions[];
  /** The parsed FREE_TEXT token — used to determine where to splice the query on selection. */
  parsedToken?: Extract<ParsedToken, { type: 'FREE_TEXT' }>;
  /**
   * Character offset in the query string where a selected filter should be inserted.
   * Used by auto-inserted FreeTextTokens (which have no parsedToken) to splice at the
   * correct position rather than appending to the end.
   */
  queryInsertOffset?: number;
  /** When true, the row expands with flex-grow to fill the remaining width of the filter bar. */
  isTrailing?: boolean;
  /**
   * Stable key identifying this token in the FilterBar grid.
   * Required for grid focus tracking and Shift+Arrow selection initiation.
   */
  tokenKey?: string;
}

/**
 * Inline editable input for unstructured free-text within FilterBar.
 * - Suggests filter keys matching the word at the cursor
 * - On `:` keypress, promotes the current word to a FILTER token
 * - On Backspace with empty input, deletes the preceding token
 */
export function FreeTextToken({
  initialValue = '',
  options,
  parsedToken,
  queryInsertOffset,
  isTrailing = false,
  tokenKey,
}: FreeTextTokenProps) {
  const { wrapperRef, query, dispatch, parseQuery, setActivePopover, parsedQuery } = useQueryBuilderContext();
  const { gridState, inputId, showTypeLabels, relationalFieldOptions, placeholder, autocompleteFooter } =
    useFilterBarContext();

  // Detect whether this FreeText sits inside any EXPRESSION group — if so the
  // Relational Fields Group options are suppressed, since you can't nest a
  // relational group inside an existing one.
  const insideExpression = useMemo(() => {
    const offset: number | null = parsedToken ? parsedToken.location.start.offset : (queryInsertOffset ?? null);
    if (offset === null) {
      return false;
    }
    // Capture into a non-nullable local so the closure below can reference it
    // without TypeScript reverting to `number | null` (and without leaning on
    // forbidden non-null assertions).
    const targetOffset: number = offset;
    const visit = (tokens: ParsedToken[]): boolean => {
      for (const t of tokens) {
        if (t.type !== 'EXPRESSION') {
          continue;
        }
        if (targetOffset > t.location.start.offset && targetOffset < t.location.end.offset) {
          return true;
        }
        if (visit(t.inner)) {
          return true;
        }
      }
      return false;
    };
    return visit(parsedQuery);
  }, [parsedQuery, parsedToken, queryInsertOffset]);

  // Scope field-key options to the group this FreeText sits in (e.g. inside
  // any_session(...) only session-scoped fields are offered).
  const scopedOptions = useMemo(() => {
    const offset = parsedToken ? parsedToken.location.start.offset : queryInsertOffset;
    if (offset === undefined || options === undefined) {
      return options;
    }
    const scope = scopeForExpressionType(expressionTypeAtOffset(parsedQuery, offset), relationalFieldOptions);
    return fieldOptionsForScope(options, scope);
  }, [options, parsedQuery, parsedToken, queryInsertOffset, relationalFieldOptions]);

  // Derive the full LOGIC_OPTIONS list and GROUP_INSERT_TEXT lookup based on whether
  // the surrounding FilterBar configured any Relational Fields. Plain "Group" stays
  // in the Operators category; each relational option gets its own GROUP_<value>
  // entry under a "Relational Fields" category and inserts its prefixed `value()`.
  // When this FreeText sits inside an existing EXPRESSION the relational Group
  // entries are omitted — only the plain Group / AND / OR options remain available.
  const usedExpressionTypes = useMemo(() => collectUsedExpressionTypes(parsedQuery), [parsedQuery]);
  const { LOGIC_OPTIONS, GROUP_INSERT_TEXT } = useMemo(() => {
    // Non-creatable options (creatable === false) still classify hydrated groups for scoping but
    // are withheld from the add-menu so no new group of that type can be created.
    const effectiveRelationalOptions = insideExpression
      ? []
      : (relationalFieldOptions ?? []).filter((o) => o.creatable !== false);
    const relationalGroupOptions = effectiveRelationalOptions.map((o) => ({
      value: `GROUP_${o.value}`,
      displayName: o.displayName ?? o.value,
      category: 'Relational Fields' as const,
      description: o.description ?? `Wrap the next expressions in a ${o.value}(…) group`,
      // An expression of this type is already in the query — the user can't add a
      // second one with the same prefix, so disable the option to make that clear.
      ...(usedExpressionTypes.has(o.value) ? { disabled: true } : {}),
    }));
    return {
      LOGIC_OPTIONS: [...BASE_LOGIC_OPTIONS, ...relationalGroupOptions],
      GROUP_INSERT_TEXT: {
        GROUP: '()',
        ...Object.fromEntries(effectiveRelationalOptions.map((o) => [`GROUP_${o.value}`, `${o.value}()`])),
      } as Record<string, string>,
    };
  }, [relationalFieldOptions, insideExpression, usedExpressionTypes]);
  const selCtx = useKeyboardSelectionContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const [inputValue, setInputValue] = useState(initialValue);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Sync external value changes into the uncontrolled input.
  // This allows the parsed token value to update the displayed text
  // without converting the input to fully controlled (which would
  // interfere with free typing).
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = initialValue;
      setInputWidth(inputRef.current, initialValue);
    }
    setInputValue(initialValue);
  }, [initialValue]);

  const popoverOptions = useMemo(() => {
    const lower = inputValue.toLowerCase().trim();

    // All Group-family options also surface whenever the input contains either paren
    // character — including the full pair `()` — since those are the visible delimiters
    // of a parenthesised group; AND/OR keep their letter-prefix match. An untrimmed-but-
    // non-empty input (e.g. only whitespace) falls back to the default no-filter behaviour.
    const logicOptions = LOGIC_OPTIONS.filter((o) => {
      if (!lower) {
        return true;
      }
      if (o.value.startsWith('GROUP') && (lower.includes('(') || lower.includes(')'))) {
        return true;
      }
      // Match Group-family options by their displayed label too so typing "anyspan" or
      // "nospan" surfaces them alongside the letter-prefix matches on AND / OR / Group.
      if (o.displayName.toLowerCase().includes(lower)) {
        return true;
      }
      return o.value.toLowerCase().startsWith(lower);
    });

    const fieldOptions = scopedOptions
      ? (lower
          ? scopedOptions.filter(
              (o) => o.field.toLowerCase().includes(lower) || (o.displayValue ?? '').toLowerCase().includes(lower),
            )
          : scopedOptions
        ).map((o) => {
          // Mirror LozengeFilter's key-cell type derivation: prefer the explicit valueType,
          // fall back to inferFieldType (BOOLEAN/STRING) over sample values. Always emit the
          // bare type — multi-value fields are not tagged with `[]`. Suppressed entirely
          // when the FilterBar opts out via `showTypeLabels={false}`.
          let typeLabel: string | undefined;
          if (showTypeLabels) {
            if (o.valueType) {
              typeLabel = o.valueType;
            } else if (o.sampleValues && o.sampleValues.length > 0) {
              typeLabel = inferFieldType(o.sampleValues) === 'BOOLEAN' ? 'boolean' : 'string';
            }
          }
          // Section label = the consumer-provided fieldCategory verbatim (any string); fall back to
          // the default "Fields" section when unset.
          const category = o.fieldCategory ?? 'Fields';
          return {
            value: o.field,
            displayName: o.displayValue,
            iconName: o.iconName,
            description: o.description,
            typeLabel,
            category,
          };
        })
      : [];

    const combined = [...logicOptions, ...fieldOptions];
    if (lower) {
      combined.sort(
        (a, b) =>
          matchScore(a.displayName ?? String(a.value), lower) - matchScore(b.displayName ?? String(b.value), lower),
      );
    }
    // When the popover renders grouped sections, items must be contiguous by category so the
    // keyboard nav's flat-array index matches the visual rendered order (otherwise highlight jumps
    // between sections). Items without a category come first; the rest are ordered alphabetically
    // by category label, then stay in matchScore-stable order within each category.
    const hasGrouping = combined.some((o) => 'category' in o && o.category);
    if (hasGrouping) {
      combined.sort((a, b) => {
        const aCat = 'category' in a && a.category ? a.category : '';
        const bCat = 'category' in b && b.category ? b.category : '';
        if (aCat === bCat) {
          return 0;
        }
        if (aCat === '') {
          return -1;
        }
        if (bCat === '') {
          return 1;
        }
        return aCat.localeCompare(bCat);
      });
    }

    const trimmed = inputValue.trim();
    const result = trimmed
      ? [{ value: FREE_TEXT_SEARCH_SENTINEL, displayName: `Search for field: ${trimmed}` }, ...combined]
      : combined;
    // Dedupe once so the rendered list, the arrow-key optionCount, and the Enter
    // onSelect index all agree — a field key can collide with a LOGIC_OPTIONS value
    // (or the search sentinel), which would otherwise crash the collection builder
    // and desync keyboard focus from selection.
    return dedupeOptionsByValue(result);
  }, [scopedOptions, inputValue, showTypeLabels, LOGIC_OPTIONS]);

  const isOpen = isPopoverOpen && popoverOptions.length > 0;

  function handleSelect(selected: string | number | boolean) {
    // Anchor `:focus-within` on the FilterBar before any state changes. The
    // mousedown that triggered this select can leave focus on the listbox
    // option (rendered in a popover portal *outside* `.filterBarOuter`), which
    // flips `:focus-within` off and removes the FilterBar's blue outline. The
    // outline returns once useApplyFocusOverride lands focus on the new
    // lozenge, but the gap is paint-visible — refocusing the input now keeps
    // the outline alive for the duration of the dispatch + re-render.
    inputRef.current?.focus();
    const selectedStr = String(selected);

    if (selectedStr === FREE_TEXT_SEARCH_SENTINEL) {
      const fieldName = inputValue.trim();
      const insertText = `${fieldName}:=:""`;

      let newQuery: string;
      if (parsedToken) {
        newQuery = replaceToken(query, parsedToken, insertText);
      } else if (queryInsertOffset !== undefined) {
        const before = query.slice(0, queryInsertOffset);
        const after = query.slice(queryInsertOffset);
        newQuery = `${before} ${insertText} ${after}`.trim().replace(/\s+/g, ' ');
      } else {
        newQuery = query ? `${query} ${insertText}` : insertText;
      }

      let insertStart: number;
      if (parsedToken) {
        insertStart = parsedToken.location.start.offset;
      } else if (queryInsertOffset !== undefined) {
        const trimmedBefore = query.slice(0, queryInsertOffset).trimEnd();
        insertStart = trimmedBefore.length > 0 ? trimmedBefore.length + 1 : 0;
      } else {
        insertStart = query ? query.length + 1 : 0;
      }

      const newFlatTokens = flattenTokens(parseQuery(newQuery));
      const newFilterFlatIndex = newFlatTokens.findIndex(
        (t) => t.type === 'FILTER' && t.field === fieldName && t.location.start.offset === insertStart,
      );

      if (inputRef.current) {
        inputRef.current.value = '';
        setInputWidth(inputRef.current, '');
      }
      setInputValue('');
      dispatch({
        type: 'SET_QUERY',
        query: newQuery,
        ...(newFilterFlatIndex >= 0 ? { focusOverride: { itemKey: String(newFilterFlatIndex), part: 'op' } } : {}),
      });
      setIsPopoverOpen(false);
      if (newFilterFlatIndex >= 0) {
        requestAnimationFrame(() => {
          setActivePopover({ tokenKey: String(newFilterFlatIndex), part: 'op', autoFocusFirst: true });
        });
      }
      return;
    }

    if (selectedStr in GROUP_INSERT_TEXT) {
      // Append an (optionally typed) parenthesised group at the appropriate insertion
      // point and route focus to the FreeText cell *inside* the new parens so the user
      // can immediately start typing a filter inside the group. The leading prefix
      // (anySpan / noSpan) is part of the inserted text — parseQuery picks it up via
      // the grammar's ExprPrefix token and surfaces it as `expressionType` on the
      // resulting EXPRESSION.
      //
      // Bail out if the typed prefix is already in use elsewhere in the query (the
      // option is rendered disabled; this catches the keyboard-Enter path that
      // bypasses react-aria's disabled handling).
      const prefixType = selectedStr.startsWith('GROUP_') ? selectedStr.slice('GROUP_'.length) : '';
      if (prefixType && usedExpressionTypes.has(prefixType)) {
        return;
      }
      const insertText = GROUP_INSERT_TEXT[selectedStr];
      let newQuery: string;
      let insertStart: number;
      if (parsedToken) {
        newQuery = replaceToken(query, parsedToken, insertText);
        insertStart = parsedToken.location.start.offset;
      } else if (queryInsertOffset !== undefined) {
        const before = query.slice(0, queryInsertOffset);
        const after = query.slice(queryInsertOffset);
        newQuery = `${before} ${insertText} ${after}`.trim().replace(/\s+/g, ' ');
        const trimmedBefore = query.slice(0, queryInsertOffset).trimEnd();
        insertStart = trimmedBefore.length > 0 ? trimmedBefore.length + 1 : 0;
      } else {
        newQuery = query ? `${query} ${insertText}` : insertText;
        insertStart = query ? query.length + 1 : 0;
      }

      // Locate the open paren of the newly inserted EXPRESSION in the re-parsed flat
      // list. The auto-inserted FreeText cell *inside* the parens has key
      // `ft-after-${openParenIndex}` (see FilterBar's buildAllKeys).
      const newFlatTokens = flattenTokens(parseQuery(newQuery));
      const newOpenParenIndex = newFlatTokens.findIndex(
        (t) => t.type === 'PAREN' && t.side === 'open' && t.expression.location.start.offset === insertStart,
      );

      if (inputRef.current) {
        inputRef.current.value = '';
        setInputWidth(inputRef.current, '');
      }
      setInputValue('');
      dispatch({
        type: 'SET_QUERY',
        query: newQuery,
        ...(newOpenParenIndex >= 0 ? { focusOverride: { itemKey: `ft-after-${newOpenParenIndex}` } } : {}),
      });
      setIsPopoverOpen(false);
      // The React Aria popover restores focus to its triggerRef (the originating
      // FreeText input) on close, racing with the focusOverride applied by
      // useApplyFocusOverride and landing focus back on the trigger. Re-apply the
      // focus on the next animation frames so our intent wins regardless of restore
      // order — and tolerate the case where React deferred the render past the first
      // frame by retrying on the second.
      if (newOpenParenIndex >= 0) {
        const targetKey = `ft-after-${newOpenParenIndex}`;
        const focusTarget = () =>
          document.querySelector<HTMLInputElement>(`input[data-key="${CSS.escape(targetKey)}"]`)?.focus();
        requestAnimationFrame(() => {
          focusTarget();
          requestAnimationFrame(focusTarget);
        });
      }
      return;
    }

    if (selectedStr === 'AND' || selectedStr === 'OR') {
      let newQuery: string;
      if (parsedToken) {
        newQuery = replaceToken(query, parsedToken, selectedStr);
      } else if (queryInsertOffset !== undefined) {
        const before = query.slice(0, queryInsertOffset);
        const after = query.slice(queryInsertOffset);
        newQuery = `${before} ${selectedStr} ${after}`.trim().replace(/\s+/g, ' ');
      } else {
        newQuery = query ? `${query} ${selectedStr}` : selectedStr;
      }

      // Compute where the operator landed so we can identify it in the re-parsed flat list.
      let insertStart: number;
      if (parsedToken) {
        insertStart = parsedToken.location.start.offset;
      } else if (queryInsertOffset !== undefined) {
        const trimmedBefore = query.slice(0, queryInsertOffset).trimEnd();
        insertStart = trimmedBefore.length > 0 ? trimmedBefore.length + 1 : 0;
      } else {
        insertStart = query ? query.length + 1 : 0;
      }

      const newFlatTokens = flattenTokens(parseQuery(newQuery));
      const newLogicIndex = newFlatTokens.findIndex(
        (t) => t.type === 'LOGIC_BOOL' && t.location.start.offset === insertStart,
      );

      if (inputRef.current) {
        inputRef.current.value = '';
        setInputWidth(inputRef.current, '');
      }
      setInputValue('');
      dispatch({
        type: 'SET_QUERY',
        query: newQuery,
        ...(newLogicIndex >= 0 ? { focusOverride: { itemKey: `ft-after-${newLogicIndex}` } } : {}),
      });
      setIsPopoverOpen(false);
      return;
    }

    const fieldName = selectedStr;
    // Resolve within this cell's scope first (matching the edit path), so a same-named field in
    // another scope can't supply the wrong defaultOp/valueType.
    const fieldData = scopedOptions?.find((o) => o.field === fieldName) ?? options?.find((o) => o.field === fieldName);
    let defaultOp = '=';
    if (fieldData?.allowedOperators?.length) {
      defaultOp = fieldData.allowedOperators[0];
    } else if (fieldData?.valueType === 'number') {
      defaultOp = '>=';
    }
    // Fields that opt into defaultToPresent start in the "exists" state (match any
    // value) rather than with an empty value — an empty value means "field is empty"
    // in the query payload, which is never what a just-created filter intends.
    const defaultValue = fieldData?.defaultToPresent ? PRESENT_VALUE_SENTINEL : '""';
    const insertText = `${fieldName}:${defaultOp}:${defaultValue}`;

    let newQuery: string;
    if (parsedToken) {
      newQuery = replaceToken(query, parsedToken, insertText);
    } else if (queryInsertOffset !== undefined) {
      const before = query.slice(0, queryInsertOffset);
      const after = query.slice(queryInsertOffset);
      newQuery = `${before} ${insertText} ${after}`.trim().replace(/\s+/g, ' ');
    } else {
      newQuery = query ? `${query} ${insertText}` : insertText;
    }

    // Compute the character offset in newQuery where the new token was inserted so we
    // can identify it unambiguously even when the same field already exists elsewhere.
    let insertStart: number;
    if (parsedToken) {
      // replaceToken splices at the parsed token's exact start offset.
      insertStart = parsedToken.location.start.offset;
    } else if (queryInsertOffset !== undefined) {
      // (before + ' ' + insertText + ' ' + after).trim().replace(/\s+/g, ' ')
      // Since the query is already normalised, before has no leading whitespace,
      // so the only adjustment is collapsing any trailing spaces in `before`.
      const trimmedBefore = query.slice(0, queryInsertOffset).trimEnd();
      insertStart = trimmedBefore.length > 0 ? trimmedBefore.length + 1 : 0;
    } else {
      insertStart = query ? query.length + 1 : 0;
    }

    // Find the flat index of the newly inserted FILTER token so we can open its
    // value popover. React 18 batches this setActivePopover call with the dispatch
    // below, so the LozengeFilter mounts already seeing itself as active.
    const newFlatTokens = flattenTokens(parseQuery(newQuery));
    const newFilterFlatIndex = newFlatTokens.findIndex((t) => {
      if (t.type !== 'FILTER') {
        return false;
      }
      return t.field === fieldName && t.location.start.offset === insertStart;
    });

    if (inputRef.current) {
      inputRef.current.value = '';
      setInputWidth(inputRef.current, '');
    }
    setInputValue('');

    dispatch({
      type: 'SET_QUERY',
      query: newQuery,
      // Move DOM focus to the new token's value cell immediately after the commit,
      // before the RAF below fires.  This ensures the cursor leaves the FreeTextToken
      // input on both the keyboard-Enter path (where the input never blurred) and the
      // readOnly-button path (where autoFocus does not fire).
      ...(newFilterFlatIndex >= 0 ? { focusOverride: { itemKey: String(newFilterFlatIndex), part: 'value' } } : {}),
    });
    setIsPopoverOpen(false);

    // Open the value popover after React Aria finishes restoring focus to triggerRef
    // on popover close (mouse-click path).  DOM focus has already been moved above;
    // this RAF only needs to set the logical activePopover state.
    if (newFilterFlatIndex >= 0) {
      requestAnimationFrame(() => {
        setActivePopover({ tokenKey: String(newFilterFlatIndex), part: 'value' });
      });
    }
  }

  const { focusedIndex, containerDataProps, handleKeyDown } = useAutocompleteNavigation({
    optionCount: popoverOptions.length,
    isOpen,
    onSelect: (index) => handleSelect(popoverOptions[index].value),
    onClose: () => setIsPopoverOpen(false),
  });

  const inputEl = (
    <Input
      ref={inputRef}
      className={isTrailing ? `${styles.trailingRow} ${styles.root}` : styles.root}
      defaultValue={initialValue}
      role="combobox"
      placeholder={isTrailing && !query.trim() ? placeholder : undefined}
      aria-label="Add a search term"
      aria-expanded={isOpen}
      tabIndex={0}
      aria-controls={listboxId}
      aria-autocomplete="list"
      id={isTrailing ? inputId : undefined}
      data-test-id={isTrailing ? 'query-builder-input' : undefined}
      data-freetext-token
      {...(inputValue ? { 'data-has-value': '' } : {})}
      {...(isTrailing ? { 'data-trailing': '' } : {})}
      {...containerDataProps}
      {...(tokenKey ? { 'data-key': tokenKey } : {})}
      onMouseDown={(e) => {
        // Toggle the autocomplete on every mousedown. Stop propagation (and the native
        // propagation) so the popover library's outside-pointerdown handler doesn't also
        // fire and race our toggle on the same press.
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        setIsPopoverOpen((open) => !open);
      }}
      onFocus={(e) => {
        // Honor a one-shot suppress flag set by callers that programmatically
        // focus this input as a side effect of an action (e.g. LozengeFilter's
        // remove button moves focus to the preceding FreeText) — those flows
        // shouldn't auto-open the autocomplete. The flag is consumed on the
        // first focus event; a subsequent user-initiated mousedown still opens
        // the popover via the toggle handler above.
        const target = e.currentTarget;
        if (target.dataset.suppressAutocompleteOnFocus) {
          delete target.dataset.suppressAutocompleteOnFocus;
          if (tokenKey && gridState) {
            gridState.setFocusedKey(tokenKey);
          }
          return;
        }
        setIsPopoverOpen(true);
        if (tokenKey && gridState) {
          gridState.setFocusedKey(tokenKey);
        }
      }}
      onBlur={() => {
        // Defer the close so a click on a popover option lands before the popover
        // unmounts, AND so a transient blur (e.g. react-aria restoring focus to a
        // sibling popover's trigger when its `isOpen` flips to false) doesn't close
        // a popover that has already regained focus by the time the RAF runs.
        requestAnimationFrame(() => {
          if (document.activeElement !== inputRef.current) {
            setIsPopoverOpen(false);
          }
        });
      }}
      onInput={(e) => {
        const { currentTarget } = e;
        const val = currentTarget.value;
        setInputWidth(currentTarget, val);
        setInputValue(val);
        setIsPopoverOpen(true);
      }}
      onPaste={(e) => {
        const pastedText = e.clipboardData.getData('text/plain').trim();
        if (!pastedText) {
          return;
        }
        // Intercept whenever the pasted text parses into any structured tokens (FILTER,
        // LOGIC_BOOL, or EXPRESSION) — including a complete sub-query like
        // `(service_name://:/^autofix$/)`. If it parses to nothing but FREE_TEXT, fall
        // through and let the browser's default paste fill the input verbatim.
        const parsedPasted = parseQuery(pastedText);
        const hasStructured = parsedPasted.some((t) => t.type !== 'FREE_TEXT');
        if (!hasStructured) {
          return;
        }

        e.preventDefault();
        const insertText = pastedText;

        let newQuery: string;
        let insertStart: number;
        if (parsedToken) {
          newQuery = replaceToken(query, parsedToken, insertText);
          insertStart = parsedToken.location.start.offset;
        } else if (queryInsertOffset !== undefined) {
          const before = query.slice(0, queryInsertOffset);
          const after = query.slice(queryInsertOffset);
          newQuery = `${before} ${insertText} ${after}`.trim().replace(/\s+/g, ' ');
          const trimmedBefore = query.slice(0, queryInsertOffset).trimEnd();
          insertStart = trimmedBefore.length > 0 ? trimmedBefore.length + 1 : 0;
        } else {
          newQuery = query ? `${query} ${insertText}` : insertText;
          insertStart = query ? query.length + 1 : 0;
        }

        // When the pasted content resolves to a single FILTER token, focus the FreeText
        // cell after it (preserves the previous single-filter UX). For richer pastes
        // (groups, multiple filters, etc.) skip the focus override and let DOM focus
        // remain on the trailing input.
        let focusOverrideAction: { itemKey: string } | undefined;
        if (parsedPasted.length === 1 && parsedPasted[0].type === 'FILTER') {
          const filterField = parsedPasted[0].field;
          const newFlatTokens = flattenTokens(parseQuery(newQuery));
          const newFilterFlatIndex = newFlatTokens.findIndex(
            (t) => t.type === 'FILTER' && t.field === filterField && t.location.start.offset === insertStart,
          );
          if (newFilterFlatIndex >= 0) {
            focusOverrideAction = { itemKey: `ft-after-${newFilterFlatIndex}` };
          }
        }

        if (inputRef.current) {
          inputRef.current.value = '';
          setInputWidth(inputRef.current, '');
        }
        setInputValue('');
        dispatch({
          type: 'SET_QUERY',
          query: newQuery,
          ...(focusOverrideAction ? { focusOverride: focusOverrideAction } : {}),
        });
        setIsPopoverOpen(false);
      }}
      onKeyDown={(e) => {
        if (isOpen) {
          handleKeyDown(e);
        }

        if (e.key === 'Enter' && isOpen && focusedIndex < 0 && inputValue.trim()) {
          e.preventDefault();
          handleSelect(FREE_TEXT_SEARCH_SENTINEL);
          return;
        }

        // With no popover open, a plain Enter runs the search: a forced COMMIT_QUERY
        // fires onChange (→ onSubmit) even when the query is unchanged since the last
        // commit, so Enter always re-applies the query like the submit button.
        if (e.key === 'Enter' && !isOpen) {
          e.preventDefault();
          dispatch({ type: 'COMMIT_QUERY', force: true });
          return;
        }

        // Shift+Arrow: initiate token selection, but only when the cursor is already
        // at the boundary of the input (position 0 for left, value.length for right).
        // When mid-input, let the browser handle text selection normally.
        if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          if (selCtx && tokenKey) {
            const input = e.currentTarget;
            const atBoundary =
              e.key === 'ArrowLeft'
                ? input.selectionStart === 0 && input.selectionEnd === 0
                : input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
            if (atBoundary) {
              e.preventDefault();
              selCtx.selectInDirection(e.key === 'ArrowLeft' ? 'left' : 'right', tokenKey);
              return;
            }
          }
        }

        // Ctrl/Cmd+Z: let the browser undo inside the input when it has uncommitted
        // text. Once the input is clean (inputValue === initialValue), propagate so
        // the FilterBar's onKeyDown can call undo() on the query history.
        if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
          if (inputValue !== initialValue) {
            e.stopPropagation();
          }
          return;
        }

        // Ctrl/Cmd+Shift+Z or Ctrl+Y (redo): mirror the undo branch — while the input
        // holds uncommitted text, let the browser redo inside it; once clean, propagate
        // so FilterBar's onKeyDown runs the query-history redo instead.
        if (
          (e.ctrlKey || e.metaKey) &&
          (((e.key === 'z' || e.key === 'Z') && e.shiftKey) || e.key === 'y' || e.key === 'Y')
        ) {
          if (inputValue !== initialValue) {
            e.stopPropagation();
          }
          return;
        }

        // Ctrl/Cmd+A: select all tokens in the grid when structured tokens exist.
        // When there are only FREE_TEXT keys (no structured tokens), let the browser
        // handle Cmd+A natively so the text inside the input gets selected.
        if ((e.ctrlKey || e.metaKey) && e.key === 'a' && selCtx) {
          const hasStructuredTokens = selCtx.allKeys.some((k) => !selCtx.isFreeTextKey(k));
          if (hasStructuredTokens) {
            e.preventDefault();
            selCtx.state.selectAll(selCtx.allKeys);
            return;
          }
          return;
        }

        const input = e.currentTarget;

        // Backspace at position 0: select the nearest prior non-FreeText token.
        // FilterBar's useEffect will then shift DOM focus to the SelectionKeyHandler,
        // so a second Backspace deletes the selection via that handler.
        if (e.key === 'Backspace' && input.selectionStart === 0 && input.selectionEnd === 0 && selCtx && tokenKey) {
          const currentIdx = selCtx.allKeys.indexOf(tokenKey);
          for (let i = currentIdx - 1; i >= 0; i--) {
            if (!selCtx.isFreeTextKey(selCtx.allKeys[i])) {
              e.preventDefault();
              selCtx.state.setSelectedKeys(new Set([selCtx.allKeys[i]]));
              break;
            }
          }
          return;
        }

        // Use [role="grid"] (FilterBar in render-prop mode) as the search root,
        // falling back to wrapperRef (set by <QueryBuilder> in static-children mode).
        const searchRoot = input.closest<HTMLElement>('[role="grid"]') ?? wrapperRef.current;
        if (!searchRoot) {
          return;
        }

        // Modified arrows are grid-level jumps, not native caret movement:
        //  - Cmd/Ctrl+←/→ jumps to the very start/end of the whole query.
        //  - Alt+←/→ hops to the previous/next free-text gap between lozenges.
        // Mid-input, native caret/word/line editing must win (Cmd+← = line start,
        // Alt+← = word left, …); only treat the modified arrow as a grid jump once
        // the caret is already at the input's boundary for that direction.
        if ((e.metaKey || e.ctrlKey || e.altKey) && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          const atBoundary =
            e.key === 'ArrowLeft'
              ? input.selectionStart === 0 && input.selectionEnd === 0
              : input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
          if (!atBoundary) {
            return;
          }
          const dir = e.key === 'ArrowLeft' ? 'left' : 'right';
          const moved =
            e.metaKey || e.ctrlKey ? focusQueryEdge(searchRoot, dir) : focusAdjacentGap(searchRoot, input, dir);
          if (moved) {
            e.preventDefault();
          }
          return;
        }

        const tokenEls = Array.from(searchRoot.querySelectorAll<HTMLElement>('[data-token-container]'));

        if (e.key === 'ArrowLeft' && input.selectionStart === 0 && input.selectionEnd === 0) {
          for (let i = tokenEls.length - 1; i >= 0; i--) {
            // DOCUMENT_POSITION_FOLLOWING: input follows tokenEls[i] → tokenEls[i] precedes input.
            // eslint-disable-next-line no-bitwise
            if (tokenEls[i].compareDocumentPosition(input) & Node.DOCUMENT_POSITION_FOLLOWING) {
              const cells = getFocusableCells(tokenEls[i]);
              if (cells.length) {
                e.preventDefault();
                cells[cells.length - 1].focus();
                break;
              }
            }
          }
        } else if (
          e.key === 'ArrowRight' &&
          input.selectionStart === input.value.length &&
          input.selectionEnd === input.value.length
        ) {
          for (const tokenEl of tokenEls) {
            // DOCUMENT_POSITION_PRECEDING: input precedes tokenEl → tokenEl follows input.
            // eslint-disable-next-line no-bitwise
            if (tokenEl.compareDocumentPosition(input) & Node.DOCUMENT_POSITION_PRECEDING) {
              const cells = getFocusableCells(tokenEl);
              if (cells.length) {
                e.preventDefault();
                cells[0].focus();
                break;
              }
            }
          }
        }
      }}
    />
  );

  return (
    // Wrapping the input and the popover's ListBox under one react-aria Autocomplete gives the
    // ListBox virtual focus, so it stops pulling real DOM focus off the input when options load
    // asynchronously. disableAutoFocusFirst + no `filter` keep react-aria's own navigation and
    // filtering inert — useAutocompleteNavigation and the app-side option filtering stay authoritative.
    <Autocomplete inputValue={inputValue} onInputChange={setInputValue} disableAutoFocusFirst>
      {gridState ? (
        /* eslint-disable-next-line jsx-a11y/interactive-supports-focus,
           jsx-a11y/click-events-have-key-events --
           role="row" is for grid semantics; the child <input> is the focusable
           element. The onClick only stops propagation so the FilterBar's
           outer click handler doesn't refocus a different input. */
        <div
          role="row"
          aria-label="Space"
          onClick={(e) => e.stopPropagation()}
          {...(tokenKey ? { 'aria-selected': gridState.isSelected(tokenKey) } : {})}
        >
          <span role="gridcell" className={styles.container}>
            {inputEl}
          </span>
        </div>
      ) : (
        inputEl
      )}
      {isOpen && (
        <AutocompletePopover
          id={listboxId}
          triggerRef={inputRef}
          isOpen
          onOpenChange={(open) => !open && setIsPopoverOpen(false)}
          onSelect={handleSelect}
          options={popoverOptions}
          groupByCategory={popoverOptions.some((o) => 'category' in o && o.category)}
          focusedIndex={focusedIndex}
          searchText={inputValue}
          footer={autocompleteFooter}
        />
      )}
    </Autocomplete>
  );
}

FreeTextToken.displayName = 'FreeTextToken';
